import { Typography } from "@mui/material";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "wouter";
import { related, type LookupWithDescription } from "../api/types";
import {
  byKey,
  useCustomerNotifiables,
  useCustomerPartitions,
  useCustomerUsers,
  useCustomerZones,
} from "../api/useCustomerChildren";
import { formatDateTime, isTemporarilyInactive } from "../components/format";
import { DenseList, PhoneLink, Row } from "../components/Rows";
import { FullPageSpinner, Notice } from "../components/States";
import TopBar from "../components/TopBar";

interface DatasheetPageProps {
  title: ReactNode;
  customer: { account?: string; name?: string | null } | undefined;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  children: ReactNode;
}

function DatasheetPage({ title, customer, isLoading, isError, isEmpty, children }: DatasheetPageProps) {
  const { id = "" } = useParams<{ id: string }>();
  return (
    <>
      <TopBar
        back={`/c/${id}`}
        title={
          <>
            {title}
            {customer && ` · ${customer.account ?? ""} ${customer.name ?? ""}`}
          </>
        }
      />
      {isLoading ? (
        <FullPageSpinner />
      ) : isError ? (
        <Notice>
          <FormattedMessage defaultMessage="The list could not be loaded." />
        </Notice>
      ) : isEmpty ? (
        <Notice>
          <FormattedMessage defaultMessage="Nothing recorded." />
        </Notice>
      ) : (
        <DenseList>{children}</DenseList>
      )}
    </>
  );
}

const lookupText = (value: LookupWithDescription | string | null | undefined) =>
  related(value)?.["localized-description"] ?? "";

const joined = (...parts: (string | null | undefined | false)[]) =>
  parts.filter(Boolean).join(" · ");

export function ZonesPage() {
  const intl = useIntl();
  const { id = "" } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useCustomerZones(id);
  const zones = [...(data?.zones ?? [])].sort(byKey((z) => z.number));
  return (
    <DatasheetPage
      title={<FormattedMessage defaultMessage="Zones" />}
      customer={data}
      isLoading={isLoading}
      isError={isError}
      isEmpty={zones.length === 0}
    >
      {zones.map((z) => {
        const inactive = isTemporarilyInactive(z["temporary-inactive-ts"]);
        return (
          <Row
            key={z.id}
            id={z.number}
            primary={z.place}
            secondary={joined(
              lookupText(z["zone-type"]),
              lookupText(z["sensor-type"]),
              inactive &&
                intl.formatMessage(
                  { defaultMessage: "inactive until {time}" },
                  { time: formatDateTime(intl, z["temporary-inactive-ts"]) },
                ),
            )}
            muted={inactive}
          />
        );
      })}
    </DatasheetPage>
  );
}

export function PartitionsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useCustomerPartitions(id);
  const partitions = [...(data?.partitions ?? [])].sort(byKey((p) => p.number));
  return (
    <DatasheetPage
      title={<FormattedMessage defaultMessage="Partitions" />}
      customer={data}
      isLoading={isLoading}
      isError={isError}
      isEmpty={partitions.length === 0}
    >
      {partitions.map((p) => (
        <Row key={p.id} id={p.number} primary={p.description} />
      ))}
    </DatasheetPage>
  );
}

export function UsersPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useCustomerUsers(id);
  const users = [...(data?.users ?? [])].sort(byKey((u) => u.number));
  return (
    <DatasheetPage
      title={<FormattedMessage defaultMessage="Users" />}
      customer={data}
      isLoading={isLoading}
      isError={isError}
      isEmpty={users.length === 0}
    >
      {users.map((u) => (
        <Row
          key={u.id}
          id={u.number}
          primary={u.name}
          secondary={
            (u.role || u.phone) && (
              <>
                {u.role}
                {u.role && u.phone && " · "}
                <PhoneLink number={u.phone} />
              </>
            )
          }
        />
      ))}
    </DatasheetPage>
  );
}

export function NotifiablesPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useCustomerNotifiables(id);
  const persons = [...(data?.["notifiable-persons"] ?? [])].sort(byKey((p) => p.idx));
  return (
    <DatasheetPage
      title={<FormattedMessage defaultMessage="Notifiable persons" />}
      customer={data}
      isLoading={isLoading}
      isError={isError}
      isEmpty={persons.length === 0}
    >
      {persons.map((p) => (
        <Row
          key={p.id}
          primary={joined(p.name, p.role)}
          secondary={
            <>
              <PhoneLink number={p.phone} />
              {p.phone && p.mobile && " · "}
              <PhoneLink number={p.mobile} />
              {p.remark && (
                <Typography variant="caption" component="div" color="text.secondary" sx={{ whiteSpace: "normal" }}>
                  {p.remark}
                </Typography>
              )}
            </>
          }
        />
      ))}
    </DatasheetPage>
  );
}
